import { isContaCaixa } from "../Reforco/contaCaixaReforcoUtils";
import { CurrencyInput, Spinner } from "@/components";
import { useClinic } from "@/components/Context/ClinicaContext";
import { useUser } from "@/components/Context/UserContext";
import { formatCurrencyBR } from "@/utils/formatUtils";
import {
  haDivergenciaMonetaria,
  diferencaCentavos,
  exigeObservacoesFechamento,
  saldoInformadoEhZero,
} from "@/utils/moneyCompare";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Grid,
  GridItem,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  FaCalendarAlt,
  FaCashRegister,
  FaClock,
  FaHospital,
  FaUser,
} from "react-icons/fa";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useEffect, useState } from "react";
import { request } from "@/services/fetch";
import useFinancialRefresh, { notifyFinancialUpdate } from '@/hooks/useFinancialRefresh';

const MSG_OBSERVACOES_OBRIGATORIAS =
  "Quando o saldo informado difere do saldo calculado, as observações são obrigatórias.";

const sanitizeDisplayText = (value) => {
  const text = String(value || "").trim();
  if (!text) return "—";
  return text
    .replace(/\b[0-9a-f]{24}\b/gi, "")
    .replace(/Pagamento Atendimento\s+[A-Za-z0-9_-]+/gi, "Pagamento Atendimento")
    .replace(/Atendimento\s+[A-Za-z0-9_-]+/gi, "Atendimento")
    .replace(/\s{2,}/g, " ")
    .trim();
};

function FechamentoCaixaModal({ onSuccess, children }) {

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [contasCaixa, setContasCaixa] = useState([]);
  const [contaCaixaId, setContaCaixaId] = useState("");
  const [loadingContas, setLoadingContas] = useState(false);
  const [erroContas, setErroContas] = useState("");
  const [saldoInformado, setSaldoInformado] = useState(0);
  const [saldoFisicoInformado, setSaldoFisicoInformado] = useState(false);
  const [observacoes, setObservacoes] = useState("");
  const [justificativaDiferenca, setJustificativaDiferenca] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [caixaInfo, setCaixaInfo] = useState(null);
  const [resumoDia, setResumoDia] = useState(null);
  const [clinica, setClinica] = useState("");
  const [logSaldoFisico, setLogSaldoFisico] = useState([]);
  // Detalhamento de entradas e saídas do período (via fluxo de caixa)
  const [detalhamentoMovimento, setDetalhamentoMovimento] = useState({
    entradas: [],
    saidas: [],
  });
  const [erroDetalhamento, setErroDetalhamento] = useState(null);

  const { selectedClinic } = useClinic();
  const { user } = useUser();
  const toast = useToast();

  const currentDate = new Date();
  const formattedDate = format(currentDate, "dd/MM/yyyy 'às' HH:mm", {
    locale: ptBR,
  });

  // Helper function to get user ID from various sources
  const getUserId = () => {
    // Try from UserContext first
    if (user) {
      if (user._id) return user._id;
      if (user.id) return user.id;
      if (user.userId) return user.userId;
    }

    // Try from localStorage as fallback
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser.user) {
          return parsedUser.user._id || parsedUser.user.id;
        }
        if (parsedUser.colaborador) {
          return parsedUser.colaborador._id || parsedUser.colaborador.id;
        }
        if (parsedUser.profissionalSaude) {
          return (
            parsedUser.profissionalSaude._id || parsedUser.profissionalSaude.id
          );
        }
        // Direct ID on the object
        return parsedUser._id || parsedUser.id || parsedUser.userId;
      } catch (e) {
        console.error("Erro ao processar usuario armazenado:", e);
      }
    }

    return null;
  };

  // Helper function to get user name
  const getUserName = () => {
    if (user) {
      return user.nome || user.name || user.nomeCompleto || "Usuário";
    }

    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser.user) {
          return parsedUser.user.nome || parsedUser.user.name || "Usuário";
        }
        if (parsedUser.colaborador) {
          return (
            parsedUser.colaborador.nome ||
            parsedUser.colaborador.name ||
            "Colaborador"
          );
        }
        if (parsedUser.profissionalSaude) {
          return (
            parsedUser.profissionalSaude.nome ||
            parsedUser.profissionalSaude.name ||
            "Profissional"
          );
        }
        return parsedUser.nome || parsedUser.name || "Usuário";
      } catch (e) {
        console.error("Erro ao processar usuario armazenado:", e);
      }
    }

    return "Não identificado";
  };

  const getCargo = () => {
    if (user?.role) return user.role;
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        return (
          parsed.user?.role ||
          parsed.colaborador?.role ||
          parsed.profissionalSaude?.role ||
          parsed.role ||
          ""
        );
      } catch (e) {
        return "";
      }
    }
    return "";
  };

  useEffect(() => {
    if (isOpen && selectedClinic?._id) {
      setClinica(selectedClinic._id);
    } else if (isOpen) {
      setClinica("");
    }
  }, [isOpen, selectedClinic]);

  const financialRevision = useFinancialRefresh();
  useEffect(() => {
    let cancelled = false;
    setContasCaixa([]);
    setErroContas("");
    setContaCaixaId("");
    if (!isOpen || !clinica) return;
    setLoadingContas(true);
    request(`api/financas/contabancaria/clinica/${clinica}`)
      .then((contas) => {
        if (cancelled) return;
        const caixas = (Array.isArray(contas) ? contas : []).filter(isContaCaixa);
        if (caixas.length > 1) {
          setErroContas('Há mais de uma conta caixa cadastrada. Regularize o cadastro antes de realizar o fechamento.');
          return;
        }
        const unicaConta = caixas.length === 1 && caixas[0].ativa !== false ? caixas[0] : null;
        setContasCaixa(unicaConta ? [unicaConta] : []);
        setContaCaixaId(unicaConta?._id || "");
      })
      .catch(() => { if (!cancelled) setErroContas("Não foi possível carregar as contas caixa. Feche e abra o fechamento para tentar novamente."); })
      .finally(() => { if (!cancelled) setLoadingContas(false); });
    return () => { cancelled = true; };
  }, [isOpen, clinica, financialRevision]);

  // Check caixa status and fetch summary when clinic changes
  useEffect(() => {
    if (isOpen && clinica) {
      fetchCaixaInfo();
      fetchResumoDia();
    }
  }, [isOpen, clinica, financialRevision]);

  // Reset ao abrir o modal
  useEffect(() => {
    if (isOpen) {
      setSaldoInformado(0);
      setSaldoFisicoInformado(false);
      setObservacoes("");
      setLogSaldoFisico([]);
      setDetalhamentoMovimento({ entradas: [], saidas: [] });
      setErroDetalhamento(null);
    }
  }, [isOpen]);

  // Buscar detalhamento de entradas/saídas quando caixaInfo estiver disponível
  useEffect(() => {
    if (
      !isOpen ||
      !clinica ||
      !caixaInfo?.aberto ||
      !caixaInfo?.ultimaAbertura?.data
    )
      return;

    const fetchDetalhamentoMovimento = async () => {
      try {
        setErroDetalhamento(null);
        const data = await request(
          `api/financas/caixa/detalhamento-movimento/${clinica}`,
        );
        setDetalhamentoMovimento({
          entradas: data.entradas || [],
          saidas: data.saidas || [],
        });
      } catch (err) {
        console.error("Erro ao buscar detalhamento do movimento:", err);
        setErroDetalhamento(
          "Não foi possível carregar o detalhamento das entradas e saídas.",
        );
        setDetalhamentoMovimento({ entradas: [], saidas: [] });
      }
    };

    fetchDetalhamentoMovimento();
  }, [isOpen, clinica, caixaInfo?.aberto, caixaInfo?.ultimaAbertura?.data, financialRevision]);

  const fetchCaixaInfo = async () => {
    if (!clinica) return;

    try {
      setLoadingData(true);
      const data = await request(`api/financas/caixa/status/${clinica}`);
      if (!data.aberto) {
        toast({
          title: "Caixa não está aberto",
          description: "É necessário abrir o caixa antes de fechá-lo.",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
        onClose();
      } else {
        setCaixaInfo(data);
      }
    } catch (error) {
      console.error("Erro ao buscar informações do caixa:", error);
      toast({
        title: "Erro",
        description: "Não foi possível buscar as informações do caixa",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingData(false);
    }
  };

  const fetchResumoDia = async () => {
    if (!clinica) return null;

    try {
      const data = await request(`api/financas/caixa/resumo-dia/${clinica}`);
      setResumoDia(data);
      return data;
    } catch (error) {
      console.error("Erro ao buscar resumo do dia:", error);
    }
    return null;
  };

  const handleSubmit = async () => {
    if (loadingContas || !contasCaixa.some((conta) => conta._id === contaCaixaId)) {
      toast({ title: "Selecione a conta caixa", status: "warning", duration: 4000 });
      return;
    }
    if (!caixaInfo || !caixaInfo.aberto) {
      toast({
        title: "Erro",
        description: "O caixa não está aberto.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const userId = getUserId();
    if (!userId) {
      toast({
        title: "Erro",
        description:
          "Não foi possível identificar o usuário. Por favor, faça login novamente.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const resumoAtual = (await fetchResumoDia()) ?? resumoDia;
    const saldoCalculadoAtual = resumoAtual?.saldoCalculado ?? 0;

    if (
      exigeObservacoesFechamento(saldoInformado, saldoCalculadoAtual) &&
      !observacoes.trim()
    ) {
      toast({
        title: "Erro",
        description: MSG_OBSERVACOES_OBRIGATORIAS,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const temDivergencia = haDivergenciaMonetaria(
      saldoInformado,
      saldoCalculadoAtual,
    );

    try {
      setLoading(true);

      const fechamentoCaixaData = {
        clinica: clinica,
        usuario: userId,
        tipo: "fechamento",
        contaCaixaId,
        data: new Date().toISOString(),
        saldoInicial: caixaInfo.ultimaAbertura?.saldoInformado || 0,
        saldoInformado: saldoInformado,
        saldoCalculado: saldoCalculadoAtual,
        totalEntradas: resumoAtual?.totalEntradas || 0,
        totalSaidas: resumoAtual?.totalSaidas || 0,
        observacoes: observacoes,
        aberturaId: caixaInfo.ultimaAbertura?._id,
        ...(temDivergencia &&
          observacoes?.trim() && {
          justificativaDiferenca: observacoes.trim(),
        }),
        ...(temDivergencia &&
          detalhamentoMovimento && {
          detalhamentoMovimento: {
            entradas: (detalhamentoMovimento.entradas || []).map((e) => ({
              data: e.data,
              tipo: e.tipo || "",
              descricao: e.descricao || "",
              valor: typeof e.valor === "number" ? e.valor : parseFloat(e.valor) || 0,
            })),
            saidas: (detalhamentoMovimento.saidas || []).map((s) => ({
              data: s.data,
              tipo: s.tipo || "",
              descricao: s.descricao || "",
              valor: typeof s.valor === "number" ? s.valor : parseFloat(s.valor) || 0,
            })),
          },
        }),
        // Log para análise de diferença no balanço: usuário, clínica, data, caixa e lista de valores digitados
        logSaldoFisico: {
          usuario: userId,
          usuarioNome: getUserName(),
          clinica: clinica,
          dataFechamento: new Date().toISOString(),
          caixaAberturaId: caixaInfo.ultimaAbertura?._id,
          listaValores: logSaldoFisico,
        },
      };

      await request(`api/financas/caixa/fechar`, {
        method: "POST",
        body: JSON.stringify(fechamentoCaixaData),
      });

      toast({
        title: "Sucesso",
        description: "Caixa fechado com sucesso!",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      notifyFinancialUpdate(clinica);
      onClose();
      Promise.resolve().then(() => onSuccess?.()).catch(console.error);
    } catch (error) {
      console.error("Erro ao fechar caixa:", error);
      toast({
        title: "Erro",
        description: error.message || "Não foi possível fechar o caixa",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const safeFormatHora = (dateVal) => {
    if (!dateVal) return "—";
    try {
      const d = new Date(dateVal);
      return isNaN(d.getTime()) ? "—" : format(d, "HH:mm", { locale: ptBR });
    } catch {
      return "—";
    }
  };


  const handleSaldoFisicoChange = (centavos) => {
    setSaldoFisicoInformado(true);
    setSaldoInformado(centavos);
    setLogSaldoFisico((prev) => [
      ...prev,
      {
        valor: formatCurrencyBR(centavos),
        dataHora: new Date().toISOString(),
      },
    ]);
  };

  const saldoCalculadoCentavos = resumoDia?.saldoCalculado ?? 0;
  const saldoInformadoCentavos = saldoInformado;
  const diferencaCentavosValor = diferencaCentavos(
    saldoInformadoCentavos,
    saldoCalculadoCentavos,
  );

  const temDivergencia = haDivergenciaMonetaria(
    saldoInformadoCentavos,
    saldoCalculadoCentavos,
  );
  const exigeObservacoes = exigeObservacoesFechamento(
    saldoInformadoCentavos,
    saldoCalculadoCentavos,
  );
  const fechamentoZeradoConferido =
    saldoFisicoInformado &&
    saldoInformadoEhZero(saldoInformadoCentavos) &&
    !temDivergencia;

  const nomeClinicaSelecionada = selectedClinic
    ? selectedClinic.razaoSocial || selectedClinic.nome || ""
    : "";

  const resumoStatus = temDivergencia
    ? "warning"
    : fechamentoZeradoConferido
      ? "info"
      : saldoFisicoInformado
        ? "success"
        : "info";

  const InfoRow = ({ icon, label, children }) => (
    <HStack align="flex-start" spacing={2} w="100%">
      <Icon as={icon} color="red.500" mt={0.5} boxSize={4} flexShrink={0} />
      <Flex
        flex={1}
        justify="space-between"
        align="flex-start"
        gap={2}
        flexWrap="wrap"
        minW={0}
      >
        <Text
          fontWeight="semibold"
          fontSize="sm"
          color="gray.700"
          whiteSpace="normal"
        >
          {label}
        </Text>
        <Text fontSize="sm" color="gray.800" textAlign={{ base: "left", md: "right" }} minW={0} flex={{ base: "1 1 100%", md: 1 }}>
          {children}
        </Text>
      </Flex>
    </HStack>
  );

  return (
    <>
      {typeof children === 'function' ? children({ onOpen }) : children}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="2xl"
        closeOnOverlayClick={false}
        blockScrollOnMount
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent
          borderRadius="lg"
          overflow="hidden"
          w={{ base: "calc(100vw - 16px)", md: "calc(100vw - 48px)" }}
          maxW="1200px"
          maxH={{ base: "calc(100dvh - 16px)", md: "calc(100dvh - 48px)" }}
          mx={{ base: 2, md: 4 }}
          my={{ base: 2, md: 6 }}
        >
          <ModalHeader
            bgGradient="linear(to-r, red.600, red.500)"
            color="white"
            py={2.5}
            px={4}
            pr={12}
            shadow="sm"
          >
            <HStack spacing={2}>
              <Flex
                align="center"
                justify="center"
                w={8}
                h={8}
                borderRadius="full"
                bg="whiteAlpha.300"
              >
                <Icon as={FaCashRegister} boxSize={3.5} />
              </Flex>
              <Text fontWeight="bold" fontSize="md">
                Fechamento de Caixa
              </Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="white" top={2} />

          <ModalBody py={3} px={{ base: 3, md: 4 }} minH={0} overflowY="auto" overflowWrap="anywhere">
            {loadingData ? (
              <Flex justify="center" py={8}>
                <Spinner color="red.500" />
              </Flex>
            ) : (
              <VStack spacing={3} align="stretch">
                <FormControl isRequired isInvalid={Boolean(erroContas)}>
                  <FormLabel htmlFor="fechamento-conta-caixa">Conta caixa</FormLabel>
                  <Input id="fechamento-conta-caixa" isReadOnly
                    value={contaCaixaId ? "Conta caixa — 000 - Conta caixa" : ""}
                    placeholder={loadingContas ? "Carregando conta caixa..." : "Conta caixa indisponível"}
                    bg={contaCaixaId ? "green.50" : "gray.50"} borderColor={contaCaixaId ? "green.300" : "gray.200"} />
                  {contasCaixa[0] && <Text fontSize="xs" color="gray.600" mt={1}>
                    Agência {contasCaixa[0].agencia || '—'} · Conta {contasCaixa[0].numeroConta || '—'}{contasCaixa[0].digitoConta ? `-${contasCaixa[0].digitoConta}` : ''} · Usada automaticamente
                  </Text>}
                  <FormHelperText>{erroContas || (!loadingContas && !contasCaixa.length
                    ? "Nenhuma conta ativa cadastrada com a instituição 000 - Conta caixa nesta clínica."
                    : "O fechamento encerra o caixa geral da clínica e fica vinculado automaticamente à conta caixa.")}</FormHelperText>
                </FormControl>
                <Box
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  p={2.5}
                  bg="gray.50"
                >
                  <Grid
                    templateColumns={{ base: "1fr", md: "1fr 1fr" }}
                    gap={{ base: 2, md: 4 }}
                    alignItems="start"
                  >
                    <GridItem minW={0}>
                      <VStack align="stretch" spacing={1}>
                        <InfoRow icon={FaCalendarAlt} label="Data/Hora de fechamento:">
                          {formattedDate}
                        </InfoRow>
                        <InfoRow icon={FaHospital} label="Clínica:">
                          {nomeClinicaSelecionada || "Nenhuma clínica selecionada"}
                        </InfoRow>
                        {!clinica && (
                          <Text fontSize="xs" color="red.600" pl={6}>
                            Selecione uma clínica no seletor da tela antes de fechar
                            o caixa.
                          </Text>
                        )}
                        <InfoRow icon={FaUser} label="Colaborador/Atendente:">
                          {getUserName()}
                        </InfoRow>
                      </VStack>
                    </GridItem>

                    <GridItem
                      pt={{ base: 2, md: 0 }}
                      borderTopWidth={{ base: "1px", md: 0 }}
                      borderLeftWidth={{ base: 0, md: "1px" }}
                      borderColor="gray.300"
                      pl={{ md: 4 }}
                    >
                      <VStack align="stretch" spacing={1}>
                        {getCargo() && (
                          <InfoRow icon={FaUser} label="Cargo:">
                            {getCargo()}
                          </InfoRow>
                        )}
                        {caixaInfo?.ultimaAbertura && (
                          <InfoRow icon={FaClock} label="Abertura:">
                            {format(
                              new Date(caixaInfo.ultimaAbertura.data),
                              "dd/MM/yyyy HH:mm",
                              { locale: ptBR },
                            )}
                          </InfoRow>
                        )}
                      </VStack>
                    </GridItem>
                  </Grid>
                </Box>

                {erroDetalhamento && (
                  <Alert status="warning" borderRadius="md" size="sm" py={2}>
                    <AlertIcon />
                    <Text fontSize="sm">{erroDetalhamento}</Text>
                  </Alert>
                )}

                <Grid
                  templateColumns={{ base: "1fr", md: "1fr 1fr", lg: "1fr 1fr 1fr" }}
                  gap={3}
                  alignItems="stretch"
                >
                  <GridItem minW={0} colSpan={{ base: 1, md: 2, lg: 1 }}>
                    <Box
                      borderWidth="1px"
                      borderColor="gray.200"
                      borderRadius="md"
                      shadow="sm"
                      h="100%"
                      display="flex"
                      flexDirection="column"
                      overflow="hidden"
                    >
                      <Text
                        fontWeight="semibold"
                        fontSize="sm"
                        px={3}
                        py={2}
                        bg="gray.50"
                        borderBottomWidth="1px"
                        borderColor="gray.200"
                      >
                        Resumo de Movimentos do Dia
                      </Text>
                      <Box flex="1" overflowX="auto">
                        <Box overflowX="auto" maxW="full" tabIndex={0} role="region" aria-label="Detalhamento do fechamento">
                        <Table size="sm" minW="320px">
                          <Thead bg="gray.100">
                            <Tr>
                              <Th fontSize="xs">Descrição</Th>
                              <Th fontSize="xs" isNumeric>
                                Valor
                              </Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            <Tr>
                              <Td fontSize="sm">Saldo Inicial</Td>
                              <Td isNumeric fontSize="sm" fontWeight="semibold">
                                {formatCurrencyBR(
                                  caixaInfo?.ultimaAbertura?.saldoInformado || 0,
                                  true,
                                )}
                              </Td>
                            </Tr>
                            <Tr bg="green.50">
                              <Td fontSize="sm" color="green.700">
                                Total de Entradas
                              </Td>
                              <Td
                                isNumeric
                                fontSize="sm"
                                color="green.700"
                                fontWeight="semibold"
                              >
                                +{formatCurrencyBR(resumoDia?.totalEntradas || 0, true)}
                              </Td>
                            </Tr>
                            <Tr bg="red.50">
                              <Td fontSize="sm" color="red.700">
                                Total de Saídas
                              </Td>
                              <Td
                                isNumeric
                                fontSize="sm"
                                color="red.700"
                                fontWeight="semibold"
                              >
                                -{formatCurrencyBR(resumoDia?.totalSaidas || 0, true)}
                              </Td>
                            </Tr>
                            <Tr bg="blue.50">
                              <Td fontSize="sm" fontWeight="bold">
                                Saldo Calculado
                              </Td>
                              <Td
                                isNumeric
                                fontSize="sm"
                                fontWeight="bold"
                                color="blue.600"
                              >
                                {formatCurrencyBR(resumoDia?.saldoCalculado || 0, true)}
                              </Td>
                            </Tr>
                          </Tbody>
                        </Table></Box>
                      </Box>
                    </Box>
                  </GridItem>

                  <GridItem minW={0}>
                    <Box
                      borderWidth="1px"
                      borderColor="gray.200"
                      borderRadius="md"
                      shadow="sm"
                      h="100%"
                      display="flex"
                      flexDirection="column"
                      overflow="hidden"
                    >
                      <Flex
                        px={3}
                        py={2}
                        bg="gray.50"
                        borderBottomWidth="1px"
                        borderColor="gray.200"
                        align="center"
                        justify="space-between"
                        gap={2}
                      >
                        <Text fontWeight="semibold" fontSize="sm">
                          Detalhamento das Entradas
                        </Text>
                        <Badge colorScheme="green" fontSize="2xs">
                          {(detalhamentoMovimento?.entradas || []).length}
                        </Badge>
                      </Flex>
                      <Box flex="1" maxH="200px" overflowY="auto" overflowX="auto">
                        <Box overflowX="auto" maxW="full" tabIndex={0} role="region" aria-label="Detalhamento do fechamento">
                        <Table size="sm" minW="320px">
                          <Thead bg="gray.100" position="sticky" top={0} zIndex={1}>
                            <Tr>
                              <Th w="52px" fontSize="xs">
                                Hora
                              </Th>
                              <Th w="72px" fontSize="xs">
                                Tipo
                              </Th>
                              <Th fontSize="xs">Descrição</Th>
                              <Th w="80px" isNumeric fontSize="xs">
                                Valor
                              </Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {(detalhamentoMovimento?.entradas || []).length === 0 ? (
                              <Tr>
                                <Td
                                  colSpan={4}
                                  fontSize="xs"
                                  color="gray.500"
                                  py={3}
                                  textAlign="center"
                                >
                                  Nenhuma entrada no período
                                </Td>
                              </Tr>
                            ) : (
                              (detalhamentoMovimento?.entradas || []).map(
                                (item, idx) => (
                                  <Tr key={`entrada-${idx}`}>
                                    <Td py={1.5} fontSize="xs" color="gray.600">
                                      {safeFormatHora(item.data)}
                                    </Td>
                                    <Td py={1.5} fontSize="xs">
                                      {item.tipo || "—"}
                                    </Td>
                                    <Td py={1.5} fontSize="xs" noOfLines={2}>
                                      {sanitizeDisplayText(item.descricao || "—")}
                                    </Td>
                                    <Td
                                      py={1.5}
                                      isNumeric
                                      fontSize="xs"
                                      fontWeight="medium"
                                      color="green.600"
                                    >
                                      {formatCurrencyBR(item.valor, true)}
                                    </Td>
                                  </Tr>
                                ),
                              )
                            )}
                          </Tbody>
                        </Table></Box>
                      </Box>
                    </Box>
                  </GridItem>

                  <GridItem minW={0}>
                    <Box
                      borderWidth="1px"
                      borderColor="gray.200"
                      borderRadius="md"
                      shadow="sm"
                      h="100%"
                      display="flex"
                      flexDirection="column"
                      overflow="hidden"
                    >
                      <Flex
                        px={3}
                        py={2}
                        bg="gray.50"
                        borderBottomWidth="1px"
                        borderColor="gray.200"
                        align="center"
                        justify="space-between"
                        gap={2}
                      >
                        <Text fontWeight="semibold" fontSize="sm">
                          Detalhamento das Saídas
                        </Text>
                        <Badge colorScheme="red" fontSize="2xs">
                          {(detalhamentoMovimento?.saidas || []).length}
                        </Badge>
                      </Flex>
                      <Box flex="1" maxH="200px" overflowY="auto" overflowX="auto">
                        <Box overflowX="auto" maxW="full" tabIndex={0} role="region" aria-label="Detalhamento do fechamento">
                        <Table size="sm" minW="320px">
                          <Thead bg="gray.100" position="sticky" top={0} zIndex={1}>
                            <Tr>
                              <Th w="52px" fontSize="xs">
                                Hora
                              </Th>
                              <Th w="72px" fontSize="xs">
                                Tipo
                              </Th>
                              <Th fontSize="xs">Descrição</Th>
                              <Th w="80px" isNumeric fontSize="xs">
                                Valor
                              </Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {(detalhamentoMovimento?.saidas || []).length === 0 ? (
                              <Tr>
                                <Td
                                  colSpan={4}
                                  fontSize="xs"
                                  color="gray.500"
                                  py={3}
                                  textAlign="center"
                                >
                                  Nenhuma saída no período
                                </Td>
                              </Tr>
                            ) : (
                              (detalhamentoMovimento?.saidas || []).map((item, idx) => (
                                <Tr key={`saida-${idx}`}>
                                  <Td py={1.5} fontSize="xs" color="gray.600">
                                    {safeFormatHora(item.data)}
                                  </Td>
                                  <Td py={1.5} fontSize="xs">
                                    {item.tipo || "—"}
                                  </Td>
                                  <Td py={1.5} fontSize="xs" noOfLines={2}>
                                    {sanitizeDisplayText(item.descricao || "—")}
                                  </Td>
                                  <Td
                                    py={1.5}
                                    isNumeric
                                    fontSize="xs"
                                    fontWeight="medium"
                                    color="red.600"
                                  >
                                    {formatCurrencyBR(item.valor, true)}
                                  </Td>
                                </Tr>
                              ))
                            )}
                          </Tbody>
                        </Table></Box>
                      </Box>
                    </Box>
                  </GridItem>
                </Grid>

                {(resumoDia?.formasPagamento || []).length > 0 && (
                  <Box
                    borderWidth="1px"
                    borderColor="gray.200"
                    borderRadius="md"
                    shadow="sm"
                    overflow="hidden"
                  >
                    <Text
                      fontWeight="semibold"
                      fontSize="sm"
                      px={3}
                      py={2}
                      bg="gray.50"
                      borderBottomWidth="1px"
                      borderColor="gray.200"
                    >
                      Formas de Pagamento
                    </Text>
                    <Box overflowX="auto" maxW="full" tabIndex={0} role="region" aria-label="Detalhamento do fechamento">
                        <Table size="sm" minW="320px">
                      <Thead bg="gray.100">
                        <Tr>
                          <Th fontSize="xs">Forma de Pagamento</Th>
                          <Th fontSize="xs" isNumeric>
                            Quantidade
                          </Th>
                          <Th fontSize="xs" isNumeric>
                            Valor Total
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {resumoDia.formasPagamento.map((forma, index) => (
                          <Tr key={index}>
                            <Td fontSize="sm">{forma.tipo}</Td>
                            <Td fontSize="sm" isNumeric>
                              {forma.quantidade}
                            </Td>
                            <Td fontSize="sm" isNumeric>
                              R$ {formatCurrencyBR(forma.total)}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table></Box>
                  </Box>
                )}

                <Grid
                  templateColumns={{ base: "1fr", lg: "1fr 1.2fr 1fr" }}
                  gap={3}
                  alignItems="stretch"
                >
                  <GridItem minW={0} display="flex" flexDirection="column">
                    <FormControl isRequired mb={0} flex="1" display="flex" flexDirection="column">
                      <FormLabel mb={0.5} fontSize="sm">
                        Saldo Informado (Contagem Física)
                      </FormLabel>
                      <CurrencyInput
                        value={saldoInformado}
                        onChange={(val) => handleSaldoFisicoChange(val)}
                      />
                      <Text fontSize="xs" color="gray.500" mt={0.5}>
                        Digite o valor conferido fisicamente (notas, moedas, cartões,
                        etc.).
                      </Text>
                    </FormControl>
                  </GridItem>

                  <GridItem minW={0} display="flex" flexDirection="column">
                    <FormControl
                      isRequired={exigeObservacoes}
                      mb={0}
                      flex="1"
                      display="flex"
                      flexDirection="column"
                    >
                      <FormLabel mb={0.5} fontSize="sm">
                        Observações
                        {exigeObservacoes
                          ? " (obrigatório quando há diferença)"
                          : ""}
                      </FormLabel>
                      <Textarea
                        value={observacoes}
                        onChange={(e) => setObservacoes(e.target.value)}
                        placeholder={
                          exigeObservacoes
                            ? "Informe a justificativa para a diferença..."
                            : "Adicione observações sobre o fechamento do caixa..."
                        }
                        rows={3}
                        flex="1"
                        minH="88px"
                        size="sm"
                      />
                    </FormControl>
                  </GridItem>

                  <GridItem minW={0} display="flex" flexDirection="column">
                    <Alert
                      status={resumoStatus}
                      borderRadius="md"
                      flexDirection="column"
                      alignItems="flex-start"
                      py={2.5}
                      px={3}
                      h="100%"
                      minH="88px"
                      flex="1"
                    >
                      <HStack mb={1} spacing={2} align="flex-start" w="100%">
                        <AlertIcon boxSize={4} mt={0.5} />
                        <Box flex="1">
                          <Text fontWeight="semibold" fontSize="sm" mb={1}>
                            Importante
                          </Text>
                          <Text fontSize="xs" color="gray.700" mb={2}>
                            Ao confirmar, o sistema registrará este fechamento e não
                            será possível alterá-lo.
                          </Text>
                          <Text fontWeight="semibold" fontSize="sm">
                            {temDivergencia
                              ? "Atenção à diferença"
                              : fechamentoZeradoConferido
                                ? "Fechamento zerado"
                                : "Resumo da operação"}
                          </Text>
                        </Box>
                      </HStack>
                      <VStack align="stretch" spacing={0.5} w="100%" pl={6}>
                        <Text fontSize="xs">
                          Saldo Calculado:{" "}
                          <Text as="span" fontWeight="semibold">
                            {formatCurrencyBR(saldoCalculadoCentavos, true)}
                          </Text>
                        </Text>
                        <Text fontSize="xs">
                          Saldo Informado:{" "}
                          <Text as="span" fontWeight="semibold">
                            {formatCurrencyBR(saldoInformadoCentavos, true)}
                          </Text>
                        </Text>
                        <Text
                          fontSize="xs"
                          fontWeight="bold"
                          color={
                            temDivergencia
                              ? "orange.700"
                              : fechamentoZeradoConferido
                                ? "blue.700"
                                : "green.700"
                          }
                        >
                          Diferença:{" "}
                          {saldoFisicoInformado
                            ? `${diferencaCentavosValor >= 0 ? "+" : "-"}${formatCurrencyBR(Math.abs(diferencaCentavosValor), true)}`
                            : formatCurrencyBR(0, true)}
                        </Text>
                        {!saldoFisicoInformado ? (
                          <Text fontSize="xs" mt={0.5}>
                            Informe o saldo contado no campo Saldo Informado para
                            conferir o caixa.
                          </Text>
                        ) : temDivergencia ? (
                          <Text fontSize="xs" color="orange.700" mt={0.5}>
                            Diferença de{" "}
                            {formatCurrencyBR(Math.abs(diferencaCentavosValor), true)}.
                            Justifique nas observações para confirmar o fechamento.
                          </Text>
                        ) : fechamentoZeradoConferido ? (
                          <Text fontSize="xs" color="blue.700" mt={0.5}>
                            Dia sem movimentação ou saldo zerado. Observações
                            opcionais.
                          </Text>
                        ) : (
                          <Text fontSize="xs" color="green.700" mt={0.5}>
                            Caixa conferido. Saldo informado confere com o sistema.
                          </Text>
                        )}
                      </VStack>
                    </Alert>
                  </GridItem>
                </Grid>

              </VStack>
            )}
          </ModalBody>

          <ModalFooter
            flexShrink={0}
            flexDirection={{ base: "column", sm: "row" }}
            alignItems={{ base: "stretch", sm: "center" }}
            borderTopWidth="1px"
            borderColor="gray.200"
            py={3}
            px={4}
            gap={2}
          >
            <Button variant="outline" colorScheme="red" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              colorScheme="red"
              onClick={handleSubmit}
              isLoading={loading}
              isDisabled={
                loadingContas || !contaCaixaId ||
                loadingData ||
                !caixaInfo?.aberto ||
                !saldoFisicoInformado ||
                (exigeObservacoes && !observacoes.trim())
              }
            >
              Confirmar Fechamento
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default FechamentoCaixaModal;
